/** Shared File Intelligence helpers — consumed by ProjectInfoModal (card) and
 *  FileMetadataStrip (compact header strip). Pure logic + field-picking only;
 *  components own their JSX. See api/file_intelligence/builder.py for the source. */
import {
  ShieldCheck,
  AlertTriangle,
  Pencil,
  HelpCircle,
  Film,
  Mic,
  type LucideIcon,
} from "lucide-react";
import type { FileMetadata } from "./api";

export const DATE_CONFIDENCE_LABEL: Record<string, string> = {
  exif_original: "verified",
  exif_reencode: "re-encode date",
  filename_only: "from filename",
};

/** Human label for fm.audio.content_type. */
export const CONTENT_TYPE_LABELS: Record<string, string> = {
  short_form: "Short-form video",
  video: "Video",
  vlog: "Vlog",
  meeting: "Meeting recording",
  lecture: "Lecture",
  long_recording: "Long recording",
  voice_memo: "Voice memo",
  podcast: "Podcast",
  audio_recording: "Audio recording",
};

/** Content types that are audio-only — pick a mic icon instead of a film icon. */
export const CONTENT_TYPE_AUDIO = new Set(["voice_memo", "podcast", "audio_recording"]);

/** Friendly container label for the technical accordion. */
export const CONTAINER_LABELS: Record<string, string> = {
  qt: "qt (Apple QuickTime)",
  quicktime: "QuickTime",
  "mov,mp4,m4a,3gp,3g2,mj2": "MP4 / MOV",
  mp4: "mp4 (MPEG-4)",
  matroska: "mkv (Matroska)",
  "matroska,webm": "mkv / webm",
  webm: "webm",
};

/** Short uppercase container/extension for the content-type pill. */
const CONTAINER_EXT: Record<string, string> = {
  qt: "MOV",
  quicktime: "MOV",
};

export function shortContainer(format?: string | null, container?: string | null): string {
  const raw = (format || container || "").toLowerCase().trim();
  if (!raw) return "";
  if (CONTAINER_EXT[raw]) return CONTAINER_EXT[raw];
  const first = raw.split(/[,/]/)[0].trim();
  return (CONTAINER_EXT[first] ?? first).toUpperCase();
}

/** Compact clock: 70s -> "1:10", 3725s -> "1:02:05". */
export function formatClock(seconds?: number | null): string {
  if (!seconds || seconds <= 0) return "";
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  return h > 0
    ? `${h}:${mm}:${String(sec).padStart(2, "0")}`
    : `${mm}:${String(sec).padStart(2, "0")}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Pretty-print a capture timestamp without shifting its original local time.
 *  "2021-06-07T09:49:42-0400" -> "Jun 7, 2021 09:49". Parses Y-M-D by parts so a
 *  negative-offset local TZ can't roll the date back a day. */
export function formatCapture(s: string): string {
  const m = s.match(/(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  const ymd = m ? m[1] : (s.match(/\d{4}-\d{2}-\d{2}/)?.[0] ?? null);
  if (!ymd) return s;
  const [y, mo, d] = ymd.split("-").map(Number);
  const dateStr = y && mo && d ? `${MONTHS[mo - 1]} ${d}, ${y}` : ymd;
  return m ? `${dateStr} ${m[2]}` : dateStr;
}

export interface VerdictStyle {
  bg: string;
  color: string;
  Icon: LucideIcon;
  label: string;
}

/** Verdict colours/icon/short-label, mapped from fm.verdict[0]. */
export function getVerdictStyle(verdict?: string[]): VerdictStyle {
  switch (verdict?.[0]) {
    case "ORIGINAL":
      return { bg: "#EAF3DE", color: "#3B6D11", Icon: ShieldCheck, label: "Original" };
    case "LIKELY_ORIGINAL":
      return { bg: "#E1F5EE", color: "#0F6E56", Icon: ShieldCheck, label: "Likely original" };
    case "RE-ENCODED":
      return { bg: "#FAEEDA", color: "#854F0B", Icon: AlertTriangle, label: "Re-encoded" };
    case "MODIFIED":
      return { bg: "#FAECE7", color: "#993C1D", Icon: Pencil, label: "Modified" };
    default:
      return { bg: "#F1EFE8", color: "#5F5E5A", Icon: HelpCircle, label: verdict?.[0] ?? "Unknown" };
  }
}

/** Content-type label + icon for the pill / strip. */
export function getContentTypeMeta(ctype?: string | null): { label: string; Icon: LucideIcon } {
  const key = ctype ?? "";
  const label = CONTENT_TYPE_LABELS[key] ?? (key ? key.replace(/_/g, " ") : "Media file");
  const Icon = CONTENT_TYPE_AUDIO.has(key) ? Mic : Film;
  return { label, Icon };
}

export function getCaptured(fm: FileMetadata): string {
  return fm.time?.creation_local || fm.time?.creation_utc || fm.time?.filename_date || "";
}

export function getDateConfidenceLabel(fm: FileMetadata): string | undefined {
  return DATE_CONFIDENCE_LABEL[fm.time?.date_confidence ?? fm.date_confidence ?? ""];
}

export function getLocationText(fm: FileMetadata): string {
  const loc = fm.location;
  if (!loc) return "";
  return loc.city_display || [loc.city, loc.country].filter(Boolean).join(", ");
}

export function getDeviceText(fm: FileMetadata): string {
  return [fm.device?.make, fm.device?.model, fm.device?.software].filter(Boolean).join(", ");
}

export function getSourceText(fm: FileMetadata): string {
  const s = fm.filename_source;
  return s && s.toLowerCase() !== "unknown" ? s.charAt(0).toUpperCase() + s.slice(1) : "";
}

/** Google Maps link for the location, or null if no usable coordinates. */
export function mapsUrl(fm: FileMetadata): string | null {
  const loc = fm.location;
  return loc && loc.lat != null && loc.lon != null
    ? `https://maps.google.com/?q=${loc.lat},${loc.lon}`
    : null;
}

/** Whether a file_metadata card carries anything worth rendering. */
export function hasFileIntel(fm?: FileMetadata | null): fm is FileMetadata {
  if (!fm || fm.error || fm.extraction_method === "none") return false;
  return Boolean(
    fm.verdict?.length ||
      fm.location ||
      fm.time ||
      fm.device ||
      fm.technical ||
      fm.audio?.content_type,
  );
}
